import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { PackagePlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * After scanning a cordless tool we offer to register the charger and
 * battery packs the manufacturer lists with it — the charger being the part
 * that actually needs PAT testing, and the part everyone forgets.
 *
 * Switchable off, because a prompt you see twenty times a day stops being
 * helpful.
 */
const NameplateSettings = () => {
  const { t } = useTranslation();
  const [on, setOn] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inventory/nameplate/settings`);
      setOn(r.data.suggest_companions);
    } catch { /* the default stands */ }
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (value) => {
    const previous = on;
    setOn(value);
    setSaving(true);
    try {
      await axios.put(`${API}/inventory/nameplate/settings`, { suggest_companions: value });
    } catch (e) {
      setOn(previous);
      toast.error(e.response?.data?.detail || t('inv.common.saveFailed'));
    }
    setSaving(false);
  };

  return (
    <Card data-testid="nameplate-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <PackagePlus className="h-4 w-4" /> {t('inv.nameplate.settingsTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <label className="flex cursor-pointer items-start justify-between gap-3 rounded-lg border p-3">
          <span className="min-w-0">
            <Label className="text-sm font-medium">{t('inv.nameplate.suggestCompanions')}</Label>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {t('inv.nameplate.suggestCompanionsHint')}
            </span>
          </span>
          <Switch checked={on} disabled={!loaded || saving} onCheckedChange={save}
            data-testid="nameplate-companions-switch" />
        </label>
      </CardContent>
    </Card>
  );
};

export default NameplateSettings;
